import startCase from "lodash/startCase";
import { PRIMARY_SPORTS } from "rivals/shared/constants";
import type { PageParams } from "./types";
import { GROUPING_TYPES, TEAM_TYPES } from "./types";

/**
 * setDynamicSegments function receives a string with variables and
 * replaces these variables with their values provided in segments object
 * @param segments Record<string, string>, is an object where it holds the values of each variable in the dynamicBase string
 * @param dynamicBase str, is a string that contains variables, eg: "This is the Teams [sport] [type] [group] page"
 * in the above example the string contains multiple variables: [sport], [type], and [group]
 * For the function to return the same string but with the above variables replaced with actual values, the segments
 * objects must have all the required variables {sport: "football", type: "colleges", group: "ia"}.
 * Square brackets are unnecessary.
 * @param humanizeResult boolean | undefined, if true it will convert the end result to
 * @returns
 */
export const setDynamicSegments = (
  segments: PageParams,
  dynamicBase: string,
  humanizeResult?: boolean
): string => {
  const result = Object.keys(segments).reduce(
    (acc, key) => acc.replace(`[${key}]`, segments[key as keyof PageParams]),
    dynamicBase
  );
  return humanizeResult ? startCase(result) : result;
};

/**
 * getDefaultGroupingValue function returns the default grouping type when the page is loaded
 * depending on the value of type that we get from the page URL slug
 * @param type string | undefined, is a string that's taken from the URL slug
 * @returns GROUPING_TYPE
 */
export const getDefaultGroupingValue = (type?: string): GROUPING_TYPES => {
  return type === TEAM_TYPES.HIGH_SCHOOLS
    ? GROUPING_TYPES.BY_REGION
    : GROUPING_TYPES.BY_CONFERENCE;
};

/**
 * mapSportParamToPrimarySport is a function which main purpose is to map the sport string
 * taken from the URL slug to a PRIMARY_SPORT
 * @param sport string | undefined, is a string that's taken from the URL slug
 * @returns PRIMARY_SPORTS
 */
export const mapSportParamToPrimarySport = (sport?: string): PRIMARY_SPORTS => {
  return sport === PRIMARY_SPORTS.BASKETBALL
    ? PRIMARY_SPORTS.BASKETBALL
    : PRIMARY_SPORTS.FOOTBALL;
};

/**
 * mapTypeParamToTeamType is a function which main purpose is to map the type string
 * taken from the URL slug to a TEAM_TYPE
 * @param type string | undefined, is a string that's take from the URL slug
 * @returns TEAM_TYPES
 */
export const mapTypeParamToTeamType = (type?: string): TEAM_TYPES => {
  return type === TEAM_TYPES.HIGH_SCHOOLS
    ? TEAM_TYPES.HIGH_SCHOOLS
    : TEAM_TYPES.COLLEGES;
};

/**
 * Depending on the current sport value, getNextSport returns the other sport
 * if football it returns basketball and if basketball it returns football
 * @param currentSport PRIMARY_SPORTS, the currently selected college conferences sport
 * @returns PRIMARY_SPORTS
 */
export const getNextSport = (currentSport: PRIMARY_SPORTS): PRIMARY_SPORTS => {
  return currentSport === PRIMARY_SPORTS.FOOTBALL
    ? PRIMARY_SPORTS.BASKETBALL
    : PRIMARY_SPORTS.FOOTBALL;
};

/**
 * Depending on the current type value, getNextType returns the other team type
 * if colleges it returns highschools, and if highschools it returns colleges
 * @param currentType TEAM_TYPES, the currently selected page type
 * @returns TEAM_TYPES
 */
export const getNextType = (currentType: TEAM_TYPES): TEAM_TYPES => {
  return currentType === TEAM_TYPES.COLLEGES
    ? TEAM_TYPES.HIGH_SCHOOLS
    : TEAM_TYPES.COLLEGES;
};

/**
 * Depending on the current grouping type and the current page or view type
 * if current type is colleges and alphabetically grouping type is selected, it returns by_conference
 * if current type is highschools and alphabetically grouping type is selected, it returns by_region
 * otherwise it returns alphabetically
 * @param currentGroupingType GROUPING_TYPES, the currently selected grouping type
 * @param currentType TEAM_TYPES, the currently selected team type
 * @returns GROUPING_TYPES, the next grouping type
 */
export const getNextGroupingType = (
  currentGroupingType: GROUPING_TYPES,
  currentType: TEAM_TYPES
): GROUPING_TYPES => {
  if (
    currentType === TEAM_TYPES.COLLEGES &&
    currentGroupingType === GROUPING_TYPES.ALPHABETICALLY
  ) {
    return GROUPING_TYPES.BY_CONFERENCE;
  } else if (
    currentType === TEAM_TYPES.HIGH_SCHOOLS &&
    currentGroupingType === GROUPING_TYPES.ALPHABETICALLY
  ) {
    return GROUPING_TYPES.BY_REGION;
  }
  return GROUPING_TYPES.ALPHABETICALLY;
};
